/**
 * Local device registry for Prometheus Monitor (Phase 1).
 * Key: prometheus_device_registry_v1 — corrupt JSON → [].
 */

const REGISTRY_KEY = "prometheus_device_registry_v1";

export type DeviceKind = "esp32" | "arduino" | "camera" | "other";
export type DeviceStatus = "online" | "offline" | "unknown";
export type DeviceTransport = "usb" | "wifi" | "mqtt" | "other";

export type DeviceRecord = {
  id: string;
  alias: string;
  kind: DeviceKind;
  status: DeviceStatus;
  /** Primary owner — 1:1 with project in Phase 1. */
  projectId: string;
  lastSeenAt?: string;
  transport?: DeviceTransport;
};

const KINDS: ReadonlySet<string> = new Set(["esp32", "arduino", "camera", "other"]);
const STATUSES: ReadonlySet<string> = new Set(["online", "offline", "unknown"]);
const TRANSPORTS: ReadonlySet<string> = new Set(["usb", "wifi", "mqtt", "other"]);

function newId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `dev-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function isDeviceRecord(v: unknown): v is DeviceRecord {
  if (v == null || typeof v !== "object") return false;
  const d = v as Record<string, unknown>;
  return (
    typeof d.id === "string" &&
    typeof d.alias === "string" &&
    typeof d.projectId === "string" &&
    KINDS.has(String(d.kind)) &&
    STATUSES.has(String(d.status))
  );
}

function normalizeDevice(raw: DeviceRecord): DeviceRecord {
  const transport =
    raw.transport && TRANSPORTS.has(raw.transport) ? raw.transport : undefined;
  return {
    id: raw.id,
    alias: raw.alias,
    kind: raw.kind,
    status: raw.status,
    projectId: raw.projectId,
    lastSeenAt: typeof raw.lastSeenAt === "string" ? raw.lastSeenAt : undefined,
    transport,
  };
}

function safeParse(raw: string | null): DeviceRecord[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isDeviceRecord).map(normalizeDevice);
  } catch {
    return [];
  }
}

function readAll(): DeviceRecord[] {
  if (typeof localStorage === "undefined") return [];
  return safeParse(localStorage.getItem(REGISTRY_KEY));
}

function writeAll(devices: DeviceRecord[]): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(REGISTRY_KEY, JSON.stringify(devices));
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("prometheus-device-registry", { detail: { key: REGISTRY_KEY } })
    );
  }
}

export function listDevices(): DeviceRecord[] {
  return readAll();
}

export function listOnlineDevices(): DeviceRecord[] {
  return readAll().filter((d) => d.status === "online");
}

/**
 * Create or update a device. Prefer matching by `id`, else by `projectId` (1:1).
 * Required: `projectId`. Defaults: alias from projectId, kind "other", status "unknown", transport "usb".
 */
export function upsertDevice(
  partial: Partial<DeviceRecord> & Pick<DeviceRecord, "projectId">
): DeviceRecord {
  const devices = readAll();
  let idx = -1;
  if (partial.id) {
    idx = devices.findIndex((d) => d.id === partial.id);
  }
  if (idx < 0) {
    idx = devices.findIndex((d) => d.projectId === partial.projectId);
  }

  if (idx >= 0) {
    const prev = devices[idx];
    const updated: DeviceRecord = normalizeDevice({
      ...prev,
      ...partial,
      id: prev.id,
      projectId: partial.projectId,
      alias: partial.alias ?? prev.alias,
      kind: partial.kind ?? prev.kind,
      status: partial.status ?? prev.status,
    });
    const next = [...devices];
    next[idx] = updated;
    writeAll(next);
    return updated;
  }

  const created: DeviceRecord = normalizeDevice({
    id: partial.id ?? newId(),
    alias: partial.alias ?? partial.projectId,
    kind: partial.kind ?? "other",
    status: partial.status ?? "unknown",
    projectId: partial.projectId,
    lastSeenAt: partial.lastSeenAt,
    transport: partial.transport ?? "usb",
  });
  writeAll([...devices, created]);
  return created;
}

export function setDeviceStatus(
  id: string,
  status: DeviceStatus
): DeviceRecord | null {
  const devices = readAll();
  const idx = devices.findIndex((d) => d.id === id);
  if (idx < 0) return null;
  const updated: DeviceRecord = {
    ...devices[idx],
    status,
    lastSeenAt:
      status === "online" ? new Date().toISOString() : devices[idx].lastSeenAt,
  };
  const next = [...devices];
  next[idx] = updated;
  writeAll(next);
  return updated;
}

export function getDeviceByProject(projectId: string): DeviceRecord | null {
  return readAll().find((d) => d.projectId === projectId) ?? null;
}

export function removeDevice(id: string): boolean {
  const devices = readAll();
  const next = devices.filter((d) => d.id !== id);
  if (next.length === devices.length) return false;
  writeAll(next);
  return true;
}

/** Infer DeviceKind from wiring MCU label (case-insensitive). */
export function kindFromMcuLabel(mcuLabel: string): DeviceKind {
  const s = mcuLabel.toLowerCase();
  if (s.includes("esp32") || s.includes("esp8266")) return "esp32";
  if (s.includes("arduino")) return "arduino";
  if (s.includes("camera") || s.includes("cam ")) return "camera";
  return "other";
}

export const DEVICE_REGISTRY_KEY = REGISTRY_KEY;
